import { Box, Skeleton, Stack } from '@mui/material';
import { Action, Role, Subject, useAuth } from 'hooks/auth/useAuth';
import { memo } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

type ProtectedRouteProps =
  | {
      subject: Subject;
      action?: Action;
    }
  | {
      role: Role;
    }
  | {
      isAuthenticated: true;
    }
  | {
      permissions: { action: Action; subject: Subject }[];
      oneOf?: boolean;
    };

const ProtectedRoute = (props: ProtectedRouteProps) => {
  const { isAuthenticated, hasPermission, hasRole, isLoading } = useAuth();

  let hasAccess = false;
  if ('subject' in props) {
    hasAccess = hasPermission(props.subject, props.action ?? 'read');
  } else if ('role' in props) {
    hasAccess = hasRole(props.role);
  } else if ('isAuthenticated' in props) {
    hasAccess = isAuthenticated;
  } else if ('permissions' in props) {
    const filterMethod = props.oneOf
      ? Array.prototype.some
      : Array.prototype.every;

    hasAccess = filterMethod.call(props.permissions, (permission) => {
      return hasPermission(permission.subject, permission.action);
    });
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rounded" width={320} height={40} />
        <Skeleton variant="text" width={220} sx={{ mt: 0.5, mb: 3 }} />
        <Stack spacing={3}>
          <Skeleton variant="rounded" height={360} />
          <Skeleton variant="rounded" height={360} />
        </Stack>
      </Box>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

export default memo(ProtectedRoute);
